<script setup lang="ts">
import { api } from '@/api'
import type Message from '@/api/resources/messages/model'
import { computed, ref, watch, nextTick, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { formatToHumanReadable } from '@/utils'
import type Chatter from '@/api/resources/chatters/model'
import { ChatterStatus } from '@/api/resources/chatters/model'

const props = defineProps<{ chatter: Chatter }>()

const emit = defineEmits<{
  close: []
}>()

const currentRoute = useRoute()
const router = useRouter()
const chatId = computed(() => props.chatter.tg_peers[0].id || (currentRoute.params.id as string))

const closeChat = () => {
  if (chatId.value) {
    // If used as modal component, emit close event
    emit('close')
  } else {
    // If used as route component, navigate back
    router.push({ name: 'chats' })
  }
}

const PAGE_SIZE = 50
const messages = ref<Message[]>([])
const totalMessages = ref(0)
const isLoadingOlder = ref(false)
const hasMoreMessages = computed(() => messages.value.length < totalMessages.value)

function rowClassName(message: Message) {
  if (message.author.id === chatId.value) {
    return 'left'
  }
  return 'right'
}

async function fetchLatest() {
  const response = await api.messages.get(chatId.value, { limit: PAGE_SIZE, offset: 0 })
  if (!response?.items) return

  totalMessages.value = response.total

  if (messages.value.length === 0) {
    messages.value = response.items
    scrollToMessageInput()
  } else {
    const existingIds = new Set(messages.value.map((m) => m.id))
    const newMessages = response.items.filter((m) => !existingIds.has(m.id))
    if (newMessages.length > 0) {
      messages.value.push(...newMessages)
      scrollToMessageInput()
    }
  }
}

async function loadOlderMessages() {
  if (isLoadingOlder.value || !hasMoreMessages.value) return
  isLoadingOlder.value = true

  try {
    const response = await api.messages.get(chatId.value, {
      limit: PAGE_SIZE,
      offset: messages.value.length,
    })
    totalMessages.value = response.total

    const existingIds = new Set(messages.value.map((m) => m.id))
    const olderMessages = response.items.filter((m) => !existingIds.has(m.id))

    if (olderMessages.length > 0) {
      const scrollContainer = getScrollContainer()
      const oldScrollHeight = scrollContainer?.scrollHeight ?? 0

      messages.value.unshift(...olderMessages)

      await nextTick()
      if (scrollContainer) {
        const newScrollHeight = scrollContainer.scrollHeight
        scrollContainer.scrollTop += newScrollHeight - oldScrollHeight
      }
    }
  } finally {
    isLoadingOlder.value = false
  }
}

fetchLatest()

watch(chatId, () => {
  messages.value = []
  totalMessages.value = 0
  fetchLatest()
})

const interval = setInterval(() => {
  fetchLatest()
}, 3000)

onUnmounted(() => {
  clearInterval(interval)
})

const messageInput = ref('')
const messageInputRef = ref()

const sendMessage = async () => {
  if (!messageInput.value.trim()) return

  await api.sendMessage({
    urlParams: {
      id: props.chatter.id,
    },
    payload: { message: messageInput.value },
  })

  messageInput.value = ''
  await nextTick()
  messageInputRef.value?.focus()
}

function getScrollContainer(): HTMLElement | null {
  const element = messageInputRef.value?.$el
  if (!element) return null
  let container = element.parentElement
  while (container && container !== document.body) {
    const style = window.getComputedStyle(container)
    if (
      style.overflow === 'auto' ||
      style.overflow === 'scroll' ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll'
    ) {
      return container
    }
    container = container.parentElement
  }
  return container
}

const scrollToMessageInput = () => {
  setTimeout(() => {
    if (messageInputRef.value) {
      const element = messageInputRef.value.$el
      if (element) {
        const scrollContainer = getScrollContainer()

        if (scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect()
          const elementRect = element.getBoundingClientRect()
          const scrollTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top)

          scrollContainer.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        }
      }
    }
  }, 300)
}

const authStore = useAuthStore()
const forceActionAvailable = computed(() => authStore.user?.username === 'fedya')
const forceActionInput = ref('')
const forceAction = async () => {
  await api.forceAction({
    payload: {
      chatId: chatId.value,
      forcePrompt: forceActionInput.value,
    },
  })
  forceActionInput.value = ''
}

const availableTestNumber = computed<number | null>(() => {
  if (
    props.chatter.status !== ChatterStatus.WEBINAR_CODE_PROVIDED ||
    props.chatter.assessments.length === 5
  ) {
    return null
  }

  return (
    props.chatter.assessments.reduce((max: number, assessment) => {
      return assessment.test_id > max ? assessment.test_id : max
    }, 0) + 1
  )
})

const sendTest = async () => {
  if (!availableTestNumber.value) {
    return
  }

  await api.sendTest({
    payload: {
      chatterId: props.chatter.id,
      testNumber: availableTestNumber.value,
    },
  })
}

const currentTestNumber = computed<number | null>(() => {
  if (props.chatter.assessments.length === 0) {
    return null
  }

  return props.chatter.assessments.reduce((max: number, assessment) => {
    return assessment.test_id > max ? assessment.test_id : max
  }, 0)
})

const resendCurrentTest = async () => {
  if (!currentTestNumber.value) {
    return
  }

  await api.sendTest({
    payload: {
      chatterId: props.chatter.id,
      testNumber: currentTestNumber.value,
    },
  })
}
</script>

<template>
  <main>
    <div v-if="hasMoreMessages" style="text-align: center; margin: 1em">
      <el-button @click="loadOlderMessages" :loading="isLoadingOlder" text>
        Загрузить ранние сообщения ({{ messages.length }} из {{ totalMessages }})
      </el-button>
    </div>
    <div v-for="message in messages" :key="message.id" class="row" :class="rowClassName(message)">
      <el-card style="max-width: 500px">
        <el-space>
          <el-text>{{ message.author.username || message.author.name }}</el-text>
          <el-text>{{ formatToHumanReadable(new Date(message.timestamp)) }}</el-text>
        </el-space>
        <div class="message-text">{{ message.message }}</div>
      </el-card>
    </div>

    <div class="message-form">
      <el-input
        ref="messageInputRef"
        v-model="messageInput"
        placeholder="Напишите сообщение..."
        class="message-input"
        type="textarea"
        autosize
      />
      <el-space>
        <el-button type="primary" @click="sendMessage" :disabled="!messageInput.trim()">
          Send
        </el-button>
        <el-button @click="closeChat" type="default">Close</el-button>
      </el-space>
    </div>

    <el-space v-if="availableTestNumber || currentTestNumber">
      <el-button v-if="availableTestNumber" type="danger" @click="sendTest()"
        >Отправить обучение #{{ availableTestNumber }}</el-button
      >
      <el-button v-if="currentTestNumber" type="warning" @click="resendCurrentTest()"
        >Переотправить тест #{{ currentTestNumber }}</el-button
      >
    </el-space>

    <div v-if="forceActionAvailable" class="message-form">
      <el-input v-model="forceActionInput" placeholder="Force action" />
      <el-button type="danger" @click="forceAction">Force action</el-button>
    </div>
  </main>
</template>

<style scoped>
.row {
  display: flex;
  margin: 1em auto 0;
  left: 0;
  right: 0;
  max-width: 1000px;
}
.row.left {
  justify-content: flex-start;
}
.row.right {
  justify-content: flex-end;
}

.left .el-card {
  background: #f1fcde !important;
}
.right .el-card {
  background: #f0ebfe !important;
}

.message-text {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-form {
  display: flex;
  flex-direction: column;
  gap: 1em;
  max-width: 1000px;
  margin: 2em auto;
  align-items: flex-end;
}

.message-input {
  flex: 1;
}

.message-input :deep(.el-input__wrapper) {
  border-radius: 20px;
  transition: all 0.3s ease;
}

.message-input :deep(.el-input__wrapper):hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Responsive design */
@media (max-width: 768px) {
  .message-form-container {
    padding: 12px;
  }

  .message-form {
    gap: 8px;
  }

  .send-button {
    min-width: 80px;
  }
}
</style>
